import L, { LeafletMouseEvent } from 'leaflet';
import { CO_LOCATION_RING_PX } from './markers/shared/coLocationOffset';
import { Abgangsleitung, AnyNetworkElement, NetworkElementType, Waypoint } from '../../types/network';
import { useCouplingStore } from '../../store/couplingStore';
import { useNetworkElementsStore } from '../../store/networkElementsStore';
import { useNetworkUIStore } from '../../store/networkUIStore';

/**
 * Pixel-space hit test for map clicks (EWH feedback: cables sharing a trench
 * and tightly packed markers are indistinguishable at any zoom — coincident
 * geometry never separates, and marker icons have a fixed pixel size).
 *
 * Deliberately NO geometry manipulation (no side-by-side offset rendering):
 * the click selects the top element exactly as before, and everything else
 * within pixel tolerance is reported as "co-located" for the details panel
 * to offer as alternatives. Pure point/segment math — correct for any number
 * of overlapping elements.
 */

const CABLE_TOLERANCE_PX = 10;
// Markers of an overlap group are drawn up to CO_LOCATION_RING_PX away from their coordinate.
const MARKER_TOLERANCE_PX = 16 + CO_LOCATION_RING_PX;

/**
 * The hit test always runs in the pixel space of the map's maximum zoom level, never the
 * current one. The section exists for elements that stay indistinguishable even when the user
 * has zoomed in as far as the map allows (two Muffen on one coordinate, cables in one trench),
 * so the tolerance must be a fixed distance on the ground. Measured at the current zoom the
 * same 34 px covered hundreds of metres when zoomed out and the list held 50 elements.
 */
const HIT_TEST_ZOOM = 22;

function segmentDistancePx(p: L.Point, a: L.Point, b: L.Point): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  let t = len2 === 0 ? 0 : ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  const dx = p.x - (a.x + t * abx);
  const dy = p.y - (a.y + t * aby);
  return Math.sqrt(dx * dx + dy * dy);
}

function hitTestZoom(map: L.Map): number {
  const mapMax = map.getMaxZoom();
  return Number.isFinite(mapMax) ? Math.min(mapMax, HIT_TEST_ZOOM) : HIT_TEST_ZOOM;
}

function projectWaypoints(map: L.Map, zoom: number, waypoints?: Waypoint[] | null): L.Point[] {
  if (!Array.isArray(waypoints)) return [];
  const pts: L.Point[] = [];
  for (const wp of waypoints) {
    const lat = Number(wp?.lat);
    const lng = Number(wp?.lng);
    if (!isNaN(lat) && !isNaN(lng)) pts.push(map.project([lat, lng], zoom));
  }
  return pts;
}

/**
 * All visible elements within pixel tolerance of the click, nearest first. Distances are
 * measured at the map's maximum zoom (see HIT_TEST_ZOOM), so the result is the same at every
 * zoom level the user happens to be on.
 */
export function findElementsAtPoint(
  map: L.Map,
  clickLatLng: L.LatLng,
  elements: AnyNetworkElement[],
  visibleTypes: Set<NetworkElementType>,
): AnyNetworkElement[] {
  const zoom = hitTestZoom(map);
  const clickPt = map.project(clickLatLng, zoom);
  const hits: Array<{ element: AnyNetworkElement; distancePx: number }> = [];
  for (const el of elements) {
    if (!visibleTypes.has(el.type as NetworkElementType)) continue;
    if (el.type === 'abgangsleitung') {
      const pts = projectWaypoints(map, zoom, (el as Abgangsleitung).waypoints);
      let min = Infinity;
      for (let i = 0; i + 1 < pts.length; i++) {
        const d = segmentDistancePx(clickPt, pts[i], pts[i + 1]);
        if (d < min) min = d;
      }
      if (min <= CABLE_TOLERANCE_PX) hits.push({ element: el, distancePx: min });
    } else if ('coordinates' in el && el.coordinates) {
      const lat = Number(el.coordinates.lat);
      const lng = Number(el.coordinates.lng);
      if (isNaN(lat) || isNaN(lng)) continue;
      const d = map.project([lat, lng], zoom).distanceTo(clickPt);
      if (d <= MARKER_TOLERANCE_PX) hits.push({ element: el, distancePx: d });
    }
  }
  hits.sort((a, b) => a.distancePx - b.distancePx);
  return hits.map(h => h.element);
}

/**
 * Map-click selection: select the clicked element (unchanged behaviour) and
 * record every OTHER element under the cursor so the details panel can offer
 * them ("Weitere Elemente an dieser Stelle"). Reads the stores imperatively —
 * click-time only, no extra render subscriptions in the per-element
 * components.
 */
export function selectWithCoLocation(map: L.Map, e: LeafletMouseEvent, clicked: AnyNetworkElement): void {
  const ui = useNetworkUIStore.getState();
  let coLocated: string[] = [];
  try {
    const hits = findElementsAtPoint(
      map, e.latlng, useNetworkElementsStore.getState().elements, ui.visibleElementTypes);
    const kuppelpunktIds = new Set(useCouplingStore.getState().couplings.map(c => c.endpointElementId));
    coLocated = hits.filter(el => el.id !== clicked.id && !kuppelpunktIds.has(el.id)).map(el => el.id);
  } catch {
    // Hit test is a pure convenience — never let it break selection.
  }
  ui.setSelectedElementFromMap(clicked, coLocated);
}
